import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  tSharedAppDispatch,
  tSharedAppState,
  lessonApi,
  tPromiseFromFunctionResolved,
  userSharedSelectors,
  ILessonSession
} from 'common/modules/ReactShared';
import { lessonTaskActions } from '../../lessonTask';
import {
  isPreviewingLesson,
  lessonPreviewActions,
  storePreviewLessonId
} from '../preview';

type tLessonSession = ILessonSession | null;
const initialLessonSessionState: tLessonSession = null;

const fetchLessonSession = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof lessonApi.fetchLessonSession>['lessonSession'],
  number,
  { state: tSharedAppState }
>('lessonSession/fetchSession_request', async (lessonId, { getState }) => {
  const { lessonSession } = await lessonApi.fetchLessonSession(
    lessonId,
    isPreviewingLesson(getState(), lessonId)
  );

  return lessonSession;
});

/** Why a preview could not start: the API refused the author (`forbidden`), or anything else (`failed`). */
type tPreviewStartError = 'forbidden' | 'failed';

function previewStartError(error: unknown): tPreviewStartError {
  const status = (error as AxiosError | undefined)?.response?.status;
  // 403: not a member of the lesson's team; 404: no such lesson (or one hidden from the author).
  return status === 403 || status === 404 ? 'forbidden' : 'failed';
}

/** An author starts playing a lesson from Teach, at `taskId` or at its first task; every start is fresh. */
const startPreviewSession = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof lessonApi.startPreviewSession>['lessonSession'],
  { lessonId: number; taskId?: number },
  // No `dispatch: tSharedAppDispatch` here (or below): these thunks only dispatch plain actions, and that type
  // would keep the students app's typed dispatch (`useTypedDispatch`) from dispatching them.
  { rejectValue: tPreviewStartError }
>(
  'lessonSession/startPreview_request',
  async ({ lessonId, taskId }, { dispatch, rejectWithValue }) => {
    try {
      const { lessonSession } = await lessonApi.startPreviewSession(lessonId, taskId);
      storePreviewLessonId(lessonId);
      dispatch(lessonPreviewActions.enter(lessonId));

      return lessonSession;
    } catch (error) {
      return rejectWithValue(previewStartError(error));
    }
  }
);

/**
 * Leaves the preview: the loaders read the real endpoints again, and the preview's lesson session is dropped.
 * Every way out of a preview ends here: the Exit preview button, logging out, and navigating anywhere outside
 * the previewed lesson's tasks (see `useLessonPreviewScope`).
 */
const exitLessonPreview = createAsyncThunk<void, void>(
  'lessonSession/exitPreview',
  async (_, { dispatch }) => {
    storePreviewLessonId(null);
    dispatch(lessonPreviewActions.exit());
  }
);

const fetchLessonSessionIfAuthorized = createAsyncThunk<
  void,
  number,
  { state: tSharedAppState; dispatch: tSharedAppDispatch }
>('lessonSession/fetchSessionIfAuthorized', async (lessonId, { getState, dispatch }) => {
  const profile = userSharedSelectors.selectProfile(getState());

  if (profile) {
    await dispatch(fetchLessonSession(lessonId));
  }
});

const startLessonSession = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof lessonApi.startLessonSession>['lessonSession'],
  number
>('lessonSession/startSession_request', async (lessonId) => {
  const { lessonSession } = await lessonApi.startLessonSession(lessonId);

  return lessonSession;
});

const runLesson = createAsyncThunk<
  boolean,
  { lessonId: number; onNavigate(params: { taskId: number }): void },
  { state: tSharedAppState; dispatch: tSharedAppDispatch }
>('lessonSession/runLesson', async ({ lessonId, onNavigate }, { getState, dispatch }) => {
  const {
    lesson: { session }
  } = getState().shared;

  const needNewLessonSession = !(session && session.status === 'active');

  if (needNewLessonSession) {
    const resultAction = await dispatch(startLessonSession(lessonId));

    if (startLessonSession.fulfilled.match(resultAction) && resultAction.payload) {
      onNavigate({ taskId: resultAction.payload.taskId });
      return true;
    }
  } else if (session) {
    onNavigate({ taskId: session.taskId });
    return true;
  }

  return false;
});

const runLessonWithSessionCheck = createAsyncThunk<
  void,
  { lessonId: number; onNavigate(params: { taskId: number }): void },
  { state: tSharedAppState; dispatch: tSharedAppDispatch }
>(
  'lessonSession/runLessonWithSessionCheck',
  async ({ lessonId, onNavigate }, { dispatch }) => {
    await dispatch(fetchLessonSessionIfAuthorized(lessonId));
    await dispatch(runLesson({ lessonId, onNavigate }));
  }
);

const createCompletedLessonObjective = createAsyncThunk<
  void,
  { lessonId: number; objectiveId: number },
  { dispatch: tSharedAppDispatch }
>(
  'lessonSession/createCompletedLessonObjective_request',
  async ({ lessonId, objectiveId }, { dispatch }) => {
    await lessonApi.createCompletedLessonObjective(lessonId, objectiveId);

    await dispatch(fetchLessonSession(lessonId));
  }
);

const deleteCompletedLessonObjective = createAsyncThunk<
  void,
  { lessonId: number; objectiveId: number },
  { dispatch: tSharedAppDispatch }
>(
  'lessonSession/deleteCompletedLessonObjective_request',
  async ({ lessonId, objectiveId }, { dispatch }) => {
    await lessonApi.deleteCompletedLessonObjective(lessonId, objectiveId);

    await dispatch(fetchLessonSession(lessonId));
  }
);

const handleLessonObjectiveCheck = createAsyncThunk<
  void,
  number,
  { state: tSharedAppState; dispatch: tSharedAppDispatch }
>(
  'lessonSession/handleLessonObjectiveCheck',
  async (objectiveId, { getState, dispatch }) => {
    const {
      lesson: { data, session }
    } = getState().shared;

    if (!data) {
      return;
    }

    if (
      session?.completedObjectives &&
      session?.completedObjectives.includes(objectiveId)
    ) {
      dispatch(
        deleteCompletedLessonObjective({
          lessonId: data.id,
          objectiveId
        })
      );
    } else {
      dispatch(
        createCompletedLessonObjective({
          lessonId: data.id,
          objectiveId
        })
      );
    }
  }
);

const studentSlice = createSlice({
  name: 'lessonSession',
  initialState: initialLessonSessionState as tLessonSession,
  reducers: {
    drop: () => {
      return initialLessonSessionState;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchLessonSession.fulfilled, (_, action) => {
      return action.payload;
    });
    builder.addCase(fetchLessonSession.rejected, () => {
      return initialLessonSessionState;
    });
    builder.addCase(startLessonSession.fulfilled, (_state, action) => {
      return action.payload;
    });
    builder.addCase(startPreviewSession.fulfilled, (_state, action) => {
      return action.payload;
    });
    // Synchronous with leaving, so a page the author lands on never sees the preview's session.
    builder.addCase(lessonPreviewActions.exit, () => {
      return initialLessonSessionState;
    });
    builder.addCase(lessonTaskActions.fetchLessonTaskSession.fulfilled, (_, action) => {
      return action.payload?.taskSession?.lessonSession || initialLessonSessionState;
    });
  }
});

const {
  reducer,
  actions: { drop: dropLessonSession }
} = studentSlice;

export {
  fetchLessonSession,
  fetchLessonSessionIfAuthorized,
  startLessonSession,
  startPreviewSession,
  exitLessonPreview,
  dropLessonSession,
  runLesson,
  runLessonWithSessionCheck,
  createCompletedLessonObjective,
  deleteCompletedLessonObjective,
  handleLessonObjectiveCheck
};

export default reducer;
export type { tLessonSession, tPreviewStartError };
